import { memo, useEffect } from 'react'

import { Anchor, Center, Loader, Stack, Text } from '@mantine/core'
import { isAddressEqual } from 'viem'
import { useAccount, useConnect, useSwitchAccount } from 'wagmi'

import {
  useGetRegisteredWallet,
  useRegisterWallet,
} from '@repo/common/queries/wallet'

import { WalletConnectList } from '@/components/WalletConnectList'
import type { BasePanelProps } from '@/flows/flow_helpers'

export const PanelRegisterWallet = memo<BasePanelProps>(
  // eslint-disable-next-line complexity, max-lines-per-function
  function PanelRegisterWallet({ onCompleted }) {
    const {
      mutate: register_wallet,
      isPending,
      isSuccess,
      isError,
      error,
    } = useRegisterWallet()
    const account = useAccount()
    const registered_query = useGetRegisteredWallet()
    const connect_query = useConnect()
    const switch_query = useSwitchAccount()

    const to_register_address = account.address

    const is_matching =
      account.address != null &&
      registered_query.data != null &&
      isAddressEqual(registered_query.data.address, account.address)

    const is_mismatched =
      account.address != null &&
      registered_query.data != null &&
      !isAddressEqual(registered_query.data.address, account.address)

    useEffect(() => {
      if (is_matching) {
        onCompleted()
      }
    }, [is_matching, onCompleted])

    const registered_called = isPending || isSuccess || isError
    const registered_missing =
      registered_query.isSuccess && registered_query.data == null

    useEffect(() => {
      if (
        to_register_address != null &&
        registered_missing &&
        !registered_called
      ) {
        register_wallet({
          address: to_register_address,
          provider: account.connector?.name ?? 'unknown',
        })
      }
    }, [
      account.connector?.name,
      register_wallet,
      registered_called,
      registered_missing,
      to_register_address,
    ])

    const error_message: string = error?.message ?? ''

    const questions = (
      <Text opacity={0.7} size="md" ta="center">
        If you have any questions or issues please reach out to us at{' '}
        <Anchor href="mailto:hello@fission.xyz">hello@fission.xyz</Anchor>.
      </Text>
    )

    if (error_message) {
      return (
        <Stack gap="sm" mb="lg">
          <Text c="red" pb="lg" role="status" ta="center">
            Wallet error: {error_message}
          </Text>
          {questions}
        </Stack>
      )
    } else if (to_register_address == null) {
      return (
        <Stack gap="sm" mb="lg">
          <Text opacity={0.7} size="md" ta="center">
            Registering your wallet will connect it to your authorizations to
            use Fission&apos;s real world asset tokens
          </Text>
          <WalletConnectList
            onSelect={connect_query.connect}
            disabled={connect_query.isPending}
          />
        </Stack>
      )
    } else if (is_mismatched) {
      return (
        <Stack gap="sm" w="100%" maw={{ base: '300px', sm: '100%' }} mb="lg">
          <Text size="md" ta="center">
            Change to your previously registered wallet to continue
          </Text>
          <Text opacity={0.7} size="md" ta="center" truncate="end">
            Change to registered wallet: <br />
            {registered_query.data?.address}
          </Text>
          {questions}
          <WalletConnectList
            onSelect={switch_query.switchAccount}
            disabled={switch_query.isPending}
          />
        </Stack>
      )
    }

    return (
      <Stack gap="sm" w="100%" maw={{ base: '300px', sm: '100%' }} mb="lg">
        <Text>Registering wallet...</Text>
        <Center w="100%">
          <Loader color="primary" type="bars" />
        </Center>
      </Stack>
    )
  },
)

export function useIsMismatchedWallet() {
  const account = useAccount()

  const registered_query = useGetRegisteredWallet()

  return {
    registered_address: registered_query.data?.address,
    is_matching:
      account.address != null &&
      registered_query.data != null &&
      isAddressEqual(registered_query.data.address, account.address),
    isSuccess: registered_query.isSuccess,
  }
}

export function useRegisterWalletTitle(): string {
  const mismatch = useIsMismatchedWallet()

  if (mismatch.registered_address) return 'Switch Wallet'
  return 'Register Wallet'
}

export const useSkipRegisterWallet = () => {
  const mismatch = useIsMismatchedWallet()

  if (!mismatch.isSuccess) return false
  return mismatch.is_matching
}
